<template>
    <div class="d-flex align-items-center mb-2">
        <div v-show="isCheckbox" class="pl-1">
            <b-checkbox v-on="$listeners" :checked="$attrs.value" />
        </div>
        <b-card class="mb-0 w-100">
            <b-media no-body>
                <b-media-aside vertical-align="center">
                    <UserPicture :user="objective.user" />
                </b-media-aside>
                <b-container fluid tag="b-media-body" class="p-0">
                    <b-row no-gutters align-v="center">
                        <b-col lg="12" xl="7">
                            <b-row no-gutters>
                                <b-col>
                                    <b-link :href="goToObjectivePage(objective.id)" class="font-weight-bold">
                                        {{ objective.title }}
                                    </b-link>
                                </b-col>
                            </b-row>
                            <b-row no-gutters>
                                <b-col class="mr-5">
                                    <TextLineContainer>
                                        <RenderSimpleHtmlText>
                                            {{ $t('period', { period: objective.period.name }) }}
                                        </RenderSimpleHtmlText>
                                        <PipeDivider />
                                        <RenderSimpleHtmlText>
                                            {{ $t('responsible', { name: objective.user.name }) }}
                                        </RenderSimpleHtmlText>
                                        <span class="d-xl-none">
                                            <PipeDivider />
                                            <ObjectiveLevelText :objective-level="objective.level" />
                                        </span>
                                    </TextLineContainer>
                                </b-col>
                            </b-row>
                        </b-col>
                        <b-col lg="12" xl="5" class="mt-2 mt-xl-0">
                            <b-row no-gutters>
                                <b-col>
                                    <ProgressContainer>
                                        <ObjectiveLevelIcon slot="icon" :objective-level="objective.level" />
                                        <ProgressBar
                                            slot="progress-bar"
                                            :value="objectiveProgress"
                                            height="1rem"
                                            use-default-label
                                        />
                                        <Dropdown
                                            slot="dropdown"
                                            :on-edit-callback="onEdit"
                                            :on-delete-callback="onDelete"
                                        />
                                    </ProgressContainer>
                                </b-col>
                            </b-row>
                            <b-row no-gutters>
                                <b-col cols="12">
                                    <ButtonsContainer>
                                        <KeyResultCreateBtn
                                            slot="new-key-result-btn"
                                            id="btn-new-kr"
                                            @click="$emit('onClickNewKeyResult', objective)"
                                        />
                                        <KeyResultToggleListDropdownBtn
                                            slot="toggle-key-results-btn"
                                            id="btn-view-krs"
                                            v-b-toggle="collapseID"
                                            :isOpen="isCollapseOpen"
                                            :disabled="!isThereKeyResults"
                                        />
                                        <OKRsChartBtn
                                            slot="okrs-chart-btn"
                                            id="btn-go-to-okrs-chart"
                                            :objectiveId="objective.id"
                                        />
                                    </ButtonsContainer>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                </b-container>
            </b-media>
            <b-container fluid v-if="isThereKeyResults">
                <b-row>
                    <b-col>
                        <b-collapse v-model="isCollapseOpen" :id="collapseID">
                            <KeyResultsContainer>
                                <KeyResultsList :key-results="objective.key_results" v-slot="{ keyResult }">
                                    <KeyResultDetails
                                        :key-result="keyResult"
                                        @onDeletedKeyResult="$emit('onDeletedKeyResult', $event)"
                                    />
                                </KeyResultsList>
                            </KeyResultsContainer>
                        </b-collapse>
                    </b-col>
                </b-row>
            </b-container>
        </b-card>
    </div>
</template>

<script>
import KeyResultDetails from '../../key-result/details'
import {
    ButtonsContainer,
    ProgressContainer,
    ObjectiveLevelIcon,
    PipeDivider,
    KeyResultsContainer,
    ObjectiveLevelText,
    KeyResultsList,
} from './components'
import {
    UserPicture,
    RenderSimpleHtmlText,
    Dropdown,
    TextLineContainer,
    KeyResultCreateBtn,
    KeyResultToggleListDropdownBtn,
    OKRsChartBtn,
} from '../../components'
import { deleteObjective as deleteObjectiveFromAPI } from '../api'
import commonMessages from '../../common-messages'
import { danger, success } from '@/lib/notification'
import { getV1PathWithOrigin } from '@/router/routesToV1'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import ProgressBar from '@/views/components/progress-bar/ProgressBar'

export default {
    name: 'ObjectiveDetails',
    components: {
        KeyResultDetails,
        ProgressBar,
        KeyResultCreateBtn,
        KeyResultToggleListDropdownBtn,
        Dropdown,
        TextLineContainer,
        UserPicture,
        RenderSimpleHtmlText,
        ButtonsContainer,
        ProgressContainer,
        ObjectiveLevelIcon,
        PipeDivider,
        KeyResultsContainer,
        ObjectiveLevelText,
        OKRsChartBtn,
        KeyResultsList,
    },
    props: {
        objective: {
            type: Object,
            required: true,
        },
        isCheckbox: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            isCollapseOpen: false,
        }
    },
    computed: {
        collapseID() {
            return this.objective.id.toString()
        },
        objectiveProgress() {
            return this.objective.progress * 100
        },
        isThereKeyResults() {
            return this.objective.key_results.length > 0
        },
    },
    methods: {
        goToObjectivePage(objectiveId) {
            return `${getV1PathWithOrigin('/okr/objectives', { objectiveId })}`
        },
        onEdit() {
            this.$router.push({ path: `${this.$route.path}/objective/${this.objective.id}/edit` })
        },
        onDelete() {
            proceedWithConfirmation({
                type: 'warning',
                icon: 'warning',
                title: this.$t('okr.modal.objective.deleteConfirmation.title'),
                message: this.$t('okr.modal.objective.deleteConfirmation.message'),
                confirmButtonText: this.$t('okr.modal.objective.deleteConfirmation.confirmButtonText'),
                onConfirm: () => {
                    return deleteObjectiveFromAPI(this.objective.id)
                },
                callback: () => {
                    success(this.$t('okr.modal.objective.deleteSuccess.title'))
                    this.$emit('onDeletedObjective')
                },
                onError: () => {
                    danger(this.$t('okr.modal.objective.deleteError.message'))
                },
            })
        },
    },
    i18n: {
        messages: {
            'en-US': {
                period: '<strong>Period:</strong> {period}',
            },
            'es-ES': {
                period: '<strong>Período:</strong> {period}',
            },
            'pt-BR': {
                period: '<strong>Período:</strong> {period}',
            },
        },
        sharedMessages: commonMessages,
    },
}
</script>
